/**
 * Heuristic fallback for SuggestedReplies — runs ONLY when the assistant
 * didn't explicitly call the suggest_replies tool. Looks at the tail of
 * the message for the obvious bounded-answer patterns (yes/no, 1-N scales,
 * "X or Y?", severity ladders) and infers chips.
 *
 * Returns null if nothing confidently matches — the caller renders no chips.
 */

const MAX_TAIL_CHARS = 200;

function lastQuestion(text: string): string {
  // Grab the substring after the last sentence-ending punctuation that
  // precedes a question mark. Falls back to the trailing chunk.
  const trimmed = text.trim();
  const lastQ = trimmed.lastIndexOf('?');
  if (lastQ === -1) return trimmed.slice(-MAX_TAIL_CHARS);
  const before = trimmed.slice(0, lastQ + 1);
  const sentenceStart = Math.max(
    before.lastIndexOf('. '),
    before.lastIndexOf('! '),
    before.lastIndexOf('? ', lastQ - 1),
    before.lastIndexOf('\n'),
  );
  return before.slice(sentenceStart + 1).trim().slice(-MAX_TAIL_CHARS);
}

function titleCase(s: string): string {
  return s.charAt(0).toUpperCase() + s.slice(1).toLowerCase();
}

export function inferSuggestedReplies(messageContent: string): string[] | null {
  if (!messageContent) return null;
  const tail = lastQuestion(messageContent).toLowerCase();
  if (!tail.endsWith('?')) return null;

  // Numeric scale: "1-5", "1 to 5", "0-5", "0 to 5", "scale of 1 to 10".
  // Captures both endpoints so a 0-based symptom-severity scale ("0-5")
  // produces six chips [0..5], not the five [1..5] a 1-based-only match gave.
  const scaleMatch = tail.match(/\b([01])\s*(?:-|to|–|—)\s*(\d{1,2})\b/);
  if (scaleMatch) {
    const bottom = parseInt(scaleMatch[1], 10);
    const top = parseInt(scaleMatch[2], 10);
    if (top > bottom && top <= 10) {
      return Array.from({ length: top - bottom + 1 }, (_, i) => String(bottom + i));
    }
  }

  // Severity ladder: "mild, moderate, or severe", "low/medium/high"
  if (/\b(mild|low)\b.*\b(moderate|medium)\b.*\b(severe|high)\b/.test(tail)) {
    if (/\bsevere\b/.test(tail)) return ['Mild', 'Moderate', 'Severe'];
    return ['Low', 'Medium', 'High'];
  }

  // Time of day: "morning or evening?", "morning, afternoon, or evening?"
  if (/\bmorning\b.*\bevening\b\??$/.test(tail) && !/\bafternoon\b/.test(tail)) {
    return ['Morning', 'Evening'];
  }
  if (/\bmorning\b.*\bafternoon\b.*\bevening\b/.test(tail)) {
    return ['Morning', 'Afternoon', 'Evening'];
  }

  // Generic "X or Y?" with short alternatives — capture the two options
  // immediately before the trailing "?". Only fires when both alternatives
  // are short single words to avoid mangling longer prose.
  const orMatch = tail.match(/\b([a-z][a-z\s-]{1,15}?)\s+or\s+([a-z][a-z\s-]{1,15}?)\??$/);
  if (orMatch) {
    const a = orMatch[1].trim();
    const b = orMatch[2].trim();
    // Reject if either side has connector words that suggest it's not a real choice
    const reject = /\b(the|a|an|and|but|because|when|if|so|that|this|to|of|in|on|with|without|how|what|why)\b/;
    if (!reject.test(a) && !reject.test(b) && a.length <= 20 && b.length <= 20) {
      return [titleCase(a), titleCase(b)];
    }
  }

  // Yes/no detection — last as the catch-all for clearly polar questions.
  // Triggers on explicit "yes or no?", "y/n?", or modal openers like
  // "want me to...?", "should I...?", "do you want...?", "ready to...?"
  if (/\byes\s*(?:\/|or)\s*no\??$/.test(tail) || /\by\s*\/\s*n\??$/.test(tail)) {
    return ['Yes', 'No'];
  }
  if (/^(want me to|should i|do you want|would you like|ready to|shall i|can i|may i)\b/.test(tail)) {
    return ['Yes', 'No'];
  }

  return null;
}
